import { useCallback, useRef, useState } from 'react'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

import { useServerEvents } from '@jotta/grpc-connect-client/useServerEvents'
import type { ServerEvent } from '@jotta/grpc-connect-openapi/esm/no/jotta/openapi/event/v1/event.v1_pb'
import { FailedDownload_Reason } from '@jotta/grpc-connect-openapi/esm/no/jotta/openapi/event/v1/event.v1_pb'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@jotta/ui/Dialog'

type ShowServerEventModalProps = {
  ok: () => void
  cancel: () => void
}

const ShowServerEventModal = ({
  open,
  ok,
  cancel,
}: { open: boolean } & ShowServerEventModalProps) => {
  return (
    <Dialog modal={true} open={open}>
      <DialogContent>
        <DialogHeader showCloseButton={false}>
          <DialogTitle>
            <Trans>Your download has failed</Trans>
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div>{t`Your download failed because one or more files could not be found. Please confirm that you would like to retry without the missing files.`}</div>
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <button className="btn btn-elevated" onClick={cancel}>
              <Trans>Cancel</Trans>
            </button>
          </DialogClose>
          <button className="btn btn-primary" type="submit" onClick={ok}>
            <Trans>Ok</Trans>
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export const ServerEvents = () => {
  const [open, setOpen] = useState(false)
  const [props, setProps] = useState({
    ok: () => setOpen(false),
    cancel: () => setOpen(false),
  })
  const eventQueueRef = useRef<ServerEvent[]>([])

  const processNextEvent = useCallback(() => {
    if (eventQueueRef.current.length === 0) {
      return
    }
    const event = eventQueueRef.current.shift()!

    if (event.event.case === 'failedDownload') {
      const downloadRef = event.event.value.downloadRefEncoded
      const reason = event.event.value.reason
      if (reason === FailedDownload_Reason.MISSING_FILE) {
        setProps({
          ok: () => {
            setOpen(false)
            window.location.href = `https://zip.jotta.cloud/zip/retry/${downloadRef}?skipMissingFiles=true`
            processNextEvent() // Process next in queue
          },
          cancel: () => {
            setOpen(false)
            processNextEvent() // Process next in queue
          },
        })
        setOpen(true)
      }
    }
  }, [])

  const handleServerEvent = useCallback(
    (event: ServerEvent) => {
      eventQueueRef.current.push(event)
      processNextEvent()
    },
    [processNextEvent],
  )

  useServerEvents(handleServerEvent)

  return <ShowServerEventModal open={open} {...props} />
}
